import { useEffect, useMemo, useState, useCallback, useRef } from "react";import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import MapView from "@/components/MapView";
import DriversPanel from "@/components/DriversPanel";
import TripFormDialog from "@/components/TripFormDialog";
import { TripStatusPill } from "@/components/StatusPills";
import { STATUS_COLORS, STATUS_LABELS, DRIVER_STATUS_COLORS } from "@/lib/status";
import StatusHistoryButton from "@/components/StatusHistoryButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, MapPin, DollarSign, User, Phone, ShoppingCart, CheckCircle2, Undo2, ChevronUp, ChevronDown, Lock } from "lucide-react";
import NotesHoverTip from "@/components/NotesHoverTip";
import ReceiptLink from "@/components/ReceiptLink";
import api, { formatApiError } from "@/lib/api";
import { playDebtChime, playCreditChime, playAppointmentChime, playPhoneRing, playTrainWhistle } from "@/lib/notify";
import { toast } from "sonner";

const ACTIVE_STATUSES = ["pending", "assigned", "accepted", "arrived_pickup", "with_client", "pending_review"];
const HISTORY_STATUSES = ["completed", "cancelled"];

// Matches Tailwind's `lg` breakpoint. Kept in sync with the splitter's
// `hidden lg:flex` class so the inline width overrides only kick in on desktop.
function useIsDesktop() {
  const [is, setIs] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIs(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return is;
}

// Trims a plain address so that it stops at the word "Naples" — long trailing
// segments like ", FL 34116, EE. UU." get chopped so the trip card fits in a
// single row. Aliased addresses (starred favourites) are ALREADY short and
// don't go through this helper.
function truncateAtNaples(addr) {
  if (!addr) return "";
  const idx = addr.toLowerCase().indexOf("naples");
  if (idx === -1) return addr;
  return addr.slice(0, idx + "naples".length);
}

// Returns true if the given ISO date string falls on the same local day as today.
function isCreatedToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export default function DispatcherConsole() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  // Seen trip IDs to detect additions between polls. `null` on the first
  // load prevents any chime on initial mount (avoiding a burst of sounds
  // for pre-existing pending trips the dispatcher opens the page into).
  const seenTripIdsRef = useRef(null);
  // Maps trip id → last-observed status. Used to detect a driver hitting
  // "LLAMAR AL CLIENTE" (status → arrived_pickup) and ring the classic
  // 90's phone tone so the dispatcher looks up.
  const seenStatusesRef = useRef(null);
  // Prevents overlapping /trips + /users/drivers polls when the backend is
  // slow, so we don't pile up requests on Cloudflare's edge.
  const loadingRef = useRef(false);
  // Shared 1-second ticker so ALL trip timers re-render together every second.
  // Per-component setInterval was being throttled or lost in some browser
  // conditions in production; a single global source of truth is more reliable.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const int = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(int);
  }, []);
  const [drivers, setDrivers] = useState([]);
  const [aliases, setAliases] = useState([]);
  useEffect(() => {
    api.get("/aliases").then(({ data }) => setAliases(data || [])).catch(() => {});
  }, []);
  const aliasByAddress = useMemo(() => {
    const m = {};
    aliases.forEach((a) => {
      const key = (a.address || "").trim().toLowerCase();
      if (key) m[key] = a.name;
    });
    return m;
  }, [aliases]);
  const nameForAddress = useCallback(
    (addr) => aliasByAddress[(addr || "").trim().toLowerCase()] || null,
    [aliasByAddress]
  );
  const [view, setView] = useState("active");   // 'active' | 'history'
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const mapRef = useRef(null);

  // ---- Resizable split (list ↔ map) — dispatchers can drag the divider to
  // suit their monitor. Persisted per-browser in localStorage. Defaults to
  // ~40% (matches the previous lg:col-span-5 / xl:col-span-4 mix).
  const isDesktop = useIsDesktop();
  const SPLIT_STORAGE_KEY = "dispatch-split-pct";
  const DEFAULT_SPLIT = 40;
  const MIN_SPLIT = 22;
  const MAX_SPLIT = 70;
  const [splitPct, setSplitPct] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_SPLIT;
    const v = Number(localStorage.getItem(SPLIT_STORAGE_KEY));
    return Number.isFinite(v) && v >= MIN_SPLIT && v <= MAX_SPLIT ? v : DEFAULT_SPLIT;
  });
  useEffect(() => {
    try { localStorage.setItem(SPLIT_STORAGE_KEY, String(splitPct)); }
    catch (err) { console.debug("split-pct localStorage write skipped:", err?.message || err); }
  }, [splitPct]);
  const draggingRef = useRef(false);
  const splitContainerRef = useRef(null);
  useEffect(() => {
    const move = (clientX) => {
      if (!draggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const p = ((clientX - rect.left) / rect.width) * 100;
      setSplitPct(Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, p)));
    };
    const onMouseMove = (e) => move(e.clientX);
    const onTouchMove = (e) => { if (e.touches[0]) move(e.touches[0].clientX); };
    const stop = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
    };
  }, []);
  const startDrag = () => {
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const load = useCallback(async () => {
    // In-flight guard: if the previous poll is still hanging (slow backend
    // or Cloudflare edge hiccup) skip this tick instead of stacking. Also
    // skip while the tab is hidden — no dispatcher is looking at the map,
    // so pounding the API just heats up Cloudflare.
    if (loadingRef.current || document.hidden) return;
    loadingRef.current = true;
    try {
      const [t, d] = await Promise.all([
        api.get("/trips"),
        api.get("/users/drivers"),
      ]);
      // Detect brand-new trips (id not seen last poll) and chime once per
      // trip based on the passenger's LLC balance snapshot captured at
      // create time. Ignore additions on the very first load.
      const incoming = t.data;
      if (seenTripIdsRef.current !== null) {
        const before = seenTripIdsRef.current;
        const newOnes = incoming.filter((x) => !before.has(x.id));
        // Appointments dropping in take priority — the special 3-note chime
        // is unmistakable so dispatchers know a booked pickup just landed.
        const anyAppt = newOnes.some((x) => x.appointment_id);
        const anyDebt = newOnes.some((x) => Number(x.client_balance_snapshot || 0) > 0.01);
        const anyCredit = newOnes.some((x) => Number(x.client_balance_snapshot || 0) < -0.01);
        if (anyAppt) playAppointmentChime();
        else if (anyDebt) playDebtChime();
        else if (anyCredit) playCreditChime();
      }
      // Detect status transitions to `arrived_pickup` — driver just tapped
      // "LLAMAR AL CLIENTE". Ring the classic phone tone once. Skip on the
      // very first load so we don't ring for pre-existing arrived trips.
      if (seenStatusesRef.current !== null) {
        const prev = seenStatusesRef.current;
        const rangToCall = incoming.some(
          (t) => t.status === "arrived_pickup" && prev.get(t.id) && prev.get(t.id) !== "arrived_pickup",
        );
        if (rangToCall) playPhoneRing();
        // Driver just finished the ride and is waiting for dispatcher
        // approval → play the train-whistle chord so the dispatcher knows
        // there's a trip to approve. Same first-load skip.
        const rangToApprove = incoming.some(
          (t) => t.status === "pending_review" && prev.get(t.id) && prev.get(t.id) !== "pending_review",
        );
        if (rangToApprove) playTrainWhistle();
      }
      seenStatusesRef.current = new Map(incoming.map((x) => [x.id, x.status]));
      seenTripIdsRef.current = new Set(incoming.map((x) => x.id));
      setTrips(incoming);
      setDrivers(d.data);
    } catch (e) {
      // Background polls should NOT spam the user with error toasts on
      // transient Cloudflare / network hiccups — the built-in retry in
      // `api.js` will recover, and the next poll picks up fresh data.
      if (process.env.NODE_ENV !== "production") console.debug("dispatcher poll failed:", e?.message || e);
    } finally {
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    load();
    const int = setInterval(load, 5000);
    // dispatcher switching apps sees fresh data without waiting for the next
    // 5s tick.
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(int);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [load]);

  const focusDriver = (d) => {
    setSelectedDriverId(d.id);
    if (d.last_lat != null && d.last_lng != null) {
      mapRef.current?.focus({ lat: d.last_lat, lng: d.last_lng, zoom: 15 });
    } else {
      toast.info(`${d.name} sin ubicación GPS aún`);
    }
  };

  // Dispatcher-defined priority for the active queue: urgent actions bubble
  // to the top, "in progress" trips sink to the bottom. Anything unknown
  // falls to the very bottom. Ties keep their original chronological order.
  const ACTIVE_PRIORITY = {
    pending_review: 1,  // Aprobar
    arrived_pickup: 2,  // Llamar cliente
    pending:        3,  // Sin asignar
    with_client:    4,  // Ya tengo al cliente
    assigned:       5,  // Chofer asignado, esperando aceptación
    accepted:       6,  // Aceptado — al final de la cola
  };

  const filteredTrips = useMemo(() => {
    const baseStatuses = view === "history" ? HISTORY_STATUSES : ACTIVE_STATUSES;
    const list = trips.filter((t) => {
      if (!baseStatuses.includes(t.status)) return false;
      // Historial lateral: solo viajes creados hoy (fecha local del dispatcher)
      if (view === "history" && !isCreatedToday(t.created_at)) return false;
      if (filter !== "all" && t.status !== filter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          (t.passenger_name || "").toLowerCase().includes(s) ||
          (t.pickup_address || "").toLowerCase().includes(s) ||
          (t.dropoff_address || "").toLowerCase().includes(s) ||
          (t.driver_name || "").toLowerCase().includes(s)
        );
      }
      return true;
    });
    if (view !== "active") return list;
    // Stable sort — Array.sort is stable since ES2019, so trips with the same
    // status keep their original chronological order from the backend.
    return [...list].sort((a, b) => (
      (ACTIVE_PRIORITY[a.status] ?? 99) - (ACTIVE_PRIORITY[b.status] ?? 99)
    ));
  }, [trips, view, filter, search]);

  const counts = useMemo(() => ({
    active: trips.filter((t) => ACTIVE_STATUSES.includes(t.status)).length,
    history: trips.filter((t) => HISTORY_STATUSES.includes(t.status) && isCreatedToday(t.created_at)).length,
  }), [trips]);

  // Queued trips per driver — shown as a badge next to their name so the
  // dispatcher sees at a glance how many trips are stacked on each chofer.
  const queuedByDriver = useMemo(() => {
    const map = {};
    trips.forEach((t) => {
      if (t.queued && t.status === "assigned" && t.driver_id) {
        map[t.driver_id] = (map[t.driver_id] || 0) + 1;
      }
    });
    return map;
  }, [trips]);

  const markers = useMemo(() => {
    const items = [];
    drivers.forEach((d) => {
      // Hide drivers marked offline — a driver that clocked out shouldn't
      // still appear as a pin on the map even if we have their last known
      // location cached.
      if (d.driver_status === "offline") return;
      if (d.last_lat != null && d.last_lng != null) {
        items.push({
          id: `driver-${d.id}`,
          lat: d.last_lat,
          lng: d.last_lng,
          color: DRIVER_STATUS_COLORS[d.driver_status] || "#6B7280",
          title: `${d.name} · ${d.driver_status}`,
          iconType: "car",
          // Driver initials (max 2 chars) — replaces the previous car glyph
          // so dispatch can distinguish drivers at a glance on the map.
          label: (d.name || "?")
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((s) => s[0].toUpperCase())
            .join("") || "?",
        });
      }
    });
    // Regular trip pickups — only while the driver is still on the way
    // to the passenger. Once the driver taps "Ya tengo al cliente"
    // (status → with_client) the pickup pin should disappear from the map.
    trips
      .filter((t) => t.trip_type !== "delivery" && ["pending", "assigned", "accepted", "arrived_pickup"].includes(t.status))
      .forEach((t) => {
        if (t.pickup_lat != null && t.pickup_lng != null) {
          items.push({
            id: `pickup-${t.id}`,
            lat: t.pickup_lat,
            lng: t.pickup_lng,
            color: STATUS_COLORS[t.status],
            title: `Pickup: ${t.pickup_address}`,
            label: "P",
          });
        }
      });
    // Regular trip dropoff pins — shown while the driver is on the way with
    // the customer so the dispatcher can see where they are heading.
    trips
      .filter((t) => t.trip_type !== "delivery" && ["with_client", "pending_review"].includes(t.status))
      .forEach((t) => {
        if (t.dropoff_lat != null && t.dropoff_lng != null) {
          items.push({
            id: `dropoff-${t.id}`,
            lat: t.dropoff_lat,
            lng: t.dropoff_lng,
            color: STATUS_COLORS[t.status],
            title: `Destino: ${t.dropoff_address}`,
            label: "D",
          });
        }
      });
    // Delivery destination pins — a shopping-cart icon on the delivery address.
    // Shown for the ENTIRE active lifecycle so the dispatcher always sees where
    // the delivery has to be dropped off.
    trips
      .filter((t) => t.trip_type === "delivery" && ["pending", "assigned", "accepted", "arrived_pickup", "with_client", "pending_review"].includes(t.status))
      .forEach((t) => {
        if (t.dropoff_lat != null && t.dropoff_lng != null) {
          items.push({
            id: `delivery-${t.id}`,
            lat: t.dropoff_lat,
            lng: t.dropoff_lng,
            color: STATUS_COLORS[t.status],
            title: `Delivery: ${t.dropoff_address}`,
            iconType: "cart",
          });
        }
      });
    return items;
  }, [drivers, trips]);

  const del = async (id) => {
    if (!window.confirm("¿Cancelar este viaje? Se moverá a Historial.")) return;
    try {
      // Soft-delete: mark as cancelled instead of DELETE. The trip stays in the
      // DB and appears in `/history` and in the Historial tab. Permanent
      // deletion (admin only) is still available from the History page.
      await api.patch(`/trips/${id}`, { status: "cancelled" });
      toast.success("Viaje movido a Historial");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AppLayout>
      <div
        ref={splitContainerRef}
        className="flex flex-col lg:flex-row gap-0 flex-1 min-h-[calc(100vh-56px)]"
      >
        {/* Left: Trip list */}
        <div
          className="border-b lg:border-b-0 lg:border-r border-border flex flex-col w-full"
          style={isDesktop ? { width: `${splitPct}%` } : undefined}
          data-testid="dispatch-list-pane"
        >
          <div className="p-4 border-b border-border space-y-3 bg-card/40">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-mono text-primary tracking-widest">DISPATCH CONSOLE</div>
                <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "Manrope" }} data-testid="active-trips-heading">
                  Viajes <span className="text-primary tabular-nums">{counts.active}</span>
                </h1>
              </div>
              <Button
                onClick={() => { setEditing(null); setDialogOpen(true); }}
                data-testid="new-trip-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
              >
                <Plus className="w-4 h-4 mr-1" /> Nuevo
              </Button>
            </div>
            <Tabs value={view} onValueChange={(v) => { setView(v); setFilter("all"); }}>
              <TabsList className="grid grid-cols-2 w-full bg-input">
                <TabsTrigger value="active" data-testid="dispatch-tab-active">
                  Activos <span className="ml-1.5 text-xs font-mono text-muted-foreground">{counts.active}</span>
                </TabsTrigger>
                <TabsTrigger value="history" data-testid="dispatch-tab-history">
                  Historial hoy <span className="ml-1.5 text-xs font-mono text-muted-foreground">{counts.history}</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex gap-2">
              <Input
                placeholder="Buscar cliente, dirección, chofer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="trip-search"
                className="bg-input border-border"
              />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40 bg-input border-border" data-testid="trip-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {(view === "history" ? HISTORY_STATUSES : ACTIVE_STATUSES).map((k) => (
                    <SelectItem key={k} value={k}>{STATUS_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1.5" data-testid={view === "history" ? "dispatch-history-today-list" : "dispatch-active-list"}>
              {filteredTrips.length === 0 && (
                <div className="text-center text-muted-foreground py-16 text-sm">
                  {view === "history"
                    ? "Sin viajes completados o cancelados hoy."
                    : "Sin viajes activos. Crea uno con “Nuevo”."}
                </div>
              )}
              {filteredTrips.map((t) => (
                <TripCard
                  key={t.id}
                  trip={t}
                  drivers={drivers}
                  now={nowTick}
                  nameForAddress={nameForAddress}
                  me={user}
                  onEdit={async (opts = {}) => {
                    const force = !!opts.force;
                    try {
                      await api.post(`/trips/${t.id}/edit-lock${force ? "?force=true" : ""}`);
                      setEditing(t);
                      setDialogOpen(true);
                    } catch (e) {
                      // 409 = someone else is already editing. Tell the user
                      // to double-click to take over.
                      const other = e?.response?.data?.detail?.editing_dispatcher;
                      toast.error(
                        other?.name
                          ? `Está siendo editado por ${other.name}. Doble clic para tomarlo.`
                          : formatApiError(e),
                      );
                    }
                  }}
                  onDelete={() => del(t.id)}
                  onReload={load}
                />
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Draggable divider (desktop only) */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Redimensionar consola y mapa"
          tabIndex={0}
          data-testid="dispatch-splitter"
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          onDoubleClick={() => setSplitPct(DEFAULT_SPLIT)}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft")      setSplitPct((p) => Math.max(MIN_SPLIT, p - 2));
            else if (e.key === "ArrowRight") setSplitPct((p) => Math.min(MAX_SPLIT, p + 2));
            else if (e.key === "Home")       setSplitPct(MIN_SPLIT);
            else if (e.key === "End")        setSplitPct(MAX_SPLIT);
            else if (e.key === "Enter")      setSplitPct(DEFAULT_SPLIT);
          }}
          title="Arrastra para redimensionar · doble click para restablecer"
          className="hidden lg:flex group relative w-1.5 shrink-0 cursor-col-resize bg-border hover:bg-primary/70 focus:bg-primary/70 focus:outline-none transition-colors items-center justify-center"
        >
          {/* wider hit zone for easier grabbing */}
          <div className="absolute inset-y-0 -left-2 -right-2" />
          {/* subtle grip handle */}
          <div className="absolute top-1/2 -translate-y-1/2 h-10 w-1 rounded-full bg-primary/40 group-hover:bg-primary group-focus:bg-primary transition-colors" />
        </div>

        {/* Right: Map + Drivers */}
        <div
          className="flex flex-col w-full flex-1"
          style={isDesktop ? { width: `${100 - splitPct}%`, flex: "0 0 auto" } : undefined}
          data-testid="dispatch-map-pane"
        >
          <div className="relative flex-1 min-h-[400px]">
            <MapView ref={mapRef} markers={markers} height="100%" />
            {/* Reset map to Golden Gate City default view */}
            <button
              type="button"
              onClick={() => mapRef.current?.resetDefault()}
              className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/20 text-white text-[11px] font-mono uppercase tracking-widest hover:bg-black/90 transition-colors shadow-lg"
              data-testid="map-reset-view-btn"
              title="Volver a Golden Gate City"
            >
              <MapPin className="w-3 h-3 text-primary" /> Golden Gate
            </button>
            {/* Floating drivers panel */}
            <div className="absolute top-4 left-4">
              <DriversPanel
                drivers={drivers}
                selectedDriverId={selectedDriverId}
                onSelect={focusDriver}
                queuedByDriver={queuedByDriver}
                onToggleDriverStatus={async (drv, target) => {
                  try {
                    await api.post(`/users/${drv.id}/driver-status`, { driver_status: target });
                    toast.success(
                      target === "free"
                        ? `${drv.name} pasa a LIBRE`
                        : `${drv.name} pasa a OFFLINE`
                    );
                    load();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <TripFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          // When the dispatcher closes the dialog (save or cancel), release
          // the soft edit lock so someone else can take the trip.
          if (!open && editing?.id) {
            api.delete(`/trips/${editing.id}/edit-lock`).catch(() => {});
          }
        }}
        trip={editing}
        drivers={drivers}
        onSaved={load}
      />

      {/* Floating action button — always reachable without scrolling the list */}
      <button
        type="button"
        onClick={() => { setEditing(null); setDialogOpen(true); }}
        data-testid="new-trip-fab"
        aria-label="Nuevo viaje"
        title="Nuevo viaje"
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-2xl shadow-primary/40 ring-2 ring-primary/30 hover:bg-primary/90 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center font-bold lg:h-16 lg:w-16"
      >
        <Plus className="w-7 h-7 lg:w-8 lg:h-8" strokeWidth={3} />
        <span className="sr-only">Nuevo viaje</span>
      </button>
    </AppLayout>
  );
}

function TripTimer({ createdAt, waitingSeconds, driverId, now }) {
  // Only freeze when the trip actually HAS a driver right now. Stale
  // `waiting_seconds` from legacy trips or paths that forgot to reset it
  // (e.g. operator release) must not stop the live counter.
  const frozen = typeof waitingSeconds === "number" && !!driverId;
  if (!createdAt) return null;
  const startMs = new Date(createdAt).getTime();
  const totalSec = frozen
    ? Math.max(0, Math.floor(waitingSeconds))
    : Math.max(0, Math.floor((now - startMs) / 1000));
  const mins = Math.floor(totalSec / 60);
  // Waiting-time color: green <7 min, orange 7-16 min, red ≥16 min.
  let color, bg;
  if (mins < 7)       { color = "#10B981"; bg = "rgba(16,185,129,0.12)"; }
  else if (mins < 16) { color = "#F59E0B"; bg = "rgba(245,158,11,0.14)"; }
  else                { color = "#EF4444"; bg = "rgba(239,68,68,0.16)"; }
  let label;
  const secs = totalSec % 60;
  if (mins < 60) {
    label = `${mins}m ${String(secs).padStart(2, "0")}s`;
  } else {
    label = `${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, "0")}m ${String(secs).padStart(2, "0")}s`;
  }
  const title = frozen
    ? `Tiempo en pendiente: ${mins}m ${totalSec % 60}s (congelado al asignar chofer)`
    : `Sin chofer hace ${mins}m ${totalSec % 60}s`;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] font-bold border tabular-nums"
      style={{ color, borderColor: color, background: bg }}
      data-testid="trip-timer"
      title={title}
    >
      <span className={`w-1 h-1 rounded-full ${frozen ? "" : "animate-pulse"}`} style={{ background: color }} />
      {label}
    </span>
  );
}

function TripCard({ trip, drivers, onEdit, onDelete, onReload, now, nameForAddress, me }) {
  const pickupAlias = nameForAddress ? nameForAddress(trip.pickup_address) : null;
  const dropoffAlias = nameForAddress ? nameForAddress(trip.dropoff_address) : null;
  const color = STATUS_COLORS[trip.status] || "#9CA3AF";
  const assignDriver = async (driver_id) => {
    const unassign = driver_id === "none";
    if (unassign && trip.driver_id) {
      const ok = window.confirm(
        `¿Quitarle el viaje a ${trip.driver_name || "el chofer"}? Volverá a "pendiente" para que otro chofer lo tome.`,
      );
      if (!ok) return;
    }
    try {
      await api.patch(`/trips/${trip.id}`, { driver_id: unassign ? null : driver_id });
      toast.success(unassign ? "Chofer removido — viaje pendiente" : "Chofer asignado");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const moveQueue = async (direction) => {
    try {
      await api.post(`/trips/${trip.id}/queue-move?direction=${direction}`);
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Card
      className="border-l-4 bg-card border-border py-1.5 px-2 hover:bg-secondary/30 transition-colors"
      style={{ borderLeftColor: color }}
      data-testid={`trip-card-${trip.id}`}
    >
      {/* Row 1: status + timer + hh:mm + trip# + passenger + dispatcher + phone + balance + price */}
      <div className="flex items-center gap-1.5 min-w-0">
        <TripStatusPill status={trip.status} />
        {trip.trip_type === "delivery" && (
          <ShoppingCart
            className="w-3 h-3 text-accent flex-shrink-0"
            title="Delivery"
            data-testid={`trip-delivery-icon-${trip.id}`}
          />
        )}
        {trip.queued && trip.status === "assigned" && (
          <span className="inline-flex items-center gap-0.5 flex-shrink-0">
            <span
              data-testid={`trip-queued-pill-${trip.id}`}
              title={`En cola para ${trip.driver_name || "el chofer"} — aparece cuando termine su viaje actual`}
              className="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] font-bold border border-sky-500/60 text-sky-200 bg-sky-500/10 uppercase tracking-widest"
            >
              cola
            </span>
            <button
              type="button"
              onClick={() => moveQueue("up")}
              title="Subir en la cola de este chofer"
              data-testid={`queue-up-${trip.id}`}
              className="w-5 h-5 grid place-items-center rounded border border-border text-sky-200 hover:bg-sky-500/20"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => moveQueue("down")}
              title="Bajar en la cola de este chofer"
              data-testid={`queue-down-${trip.id}`}
              className="w-5 h-5 grid place-items-center rounded border border-border text-sky-200 hover:bg-sky-500/20"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
          </span>
        )}
        <TripTimer createdAt={trip.created_at} waitingSeconds={trip.waiting_seconds} driverId={trip.driver_id} now={now} />
        {trip.created_at && (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] font-semibold text-muted-foreground bg-secondary/40 border border-border tabular-nums flex-shrink-0"
            title={new Date(trip.created_at).toLocaleString("es-US", { timeZone: "America/New_York", hour12: false })}
            data-testid={`trip-created-hhmm-${trip.id}`}
          >
            {new Date(trip.created_at).toLocaleTimeString("es-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false })}
          </span>
        )}
        {trip.trip_number && (
          <span className="text-[10px] font-mono text-muted-foreground flex-shrink-0">{trip.trip_number}</span>
        )}
        <span className="text-sm font-semibold truncate flex-1 min-w-0">
          {trip.passenger_name || "Sin nombre"}
        </span>
        {trip.dispatcher_name && (
          <span
            className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/40 whitespace-nowrap flex-shrink-0"
            title={`Cargada por ${trip.dispatcher_name}`}
            data-testid={`trip-dispatcher-badge-${trip.id}`}
          >
            {trip.dispatcher_name}
          </span>
        )}
        {trip.passenger_phone && (
          <a
            href={`tel:${trip.passenger_phone}`}
            onClick={async (e) => {
              // Tap-to-call also toggles the shared "I'm calling" marker so
              // the rest of the dispatchers see this trip is claimed. On
              // first tap we also copy the number to clipboard for quick
              // paste elsewhere. Don't preventDefault so the tel: link
              // still fires the dialer where supported.
              try {
                if (!trip.calling_dispatcher) {
                  try { await navigator.clipboard.writeText(trip.passenger_phone); } catch (_) { /* clipboard blocked */ }
                }
                await api.post(`/trips/${trip.id}/toggle-call`);
              } catch (err) {
                toast.error("No se pudo actualizar el estado de llamada");
              }
            }}
            title={trip.calling_dispatcher ? "Tocá para liberar el cliente" : "Click para llamar y avisar que estás llamando"}
            data-testid={`copy-phone-${trip.id}`}
            className={`text-[11px] font-mono underline-offset-4 hover:underline cursor-pointer whitespace-nowrap flex-shrink-0 ${
              trip.calling_dispatcher ? "text-emerald-400 font-bold" : "text-muted-foreground hover:text-primary"
            }`}
          >
            {trip.calling_dispatcher ? `📞 ${trip.calling_dispatcher.name}` : trip.passenger_phone}
          </a>
        )}
        {/* Client LLC balance snapshot taken at trip-creation time. Red for
            outstanding debt so the dispatcher remembers to have the driver
            collect; green for pre-paid credit that will offset future rides. */}
        {Number(trip.client_balance_snapshot || 0) > 0.01 && (
          <span
            className="text-[10px] font-mono font-black uppercase tracking-wider px-1.5 py-0.5 rounded border border-red-500/50 bg-red-500/15 text-red-300 whitespace-nowrap flex-shrink-0"
            title="El pasajero tiene deuda pendiente con la LLC"
            data-testid={`client-debt-badge-${trip.id}`}
          >
            debe ${Number(trip.client_balance_snapshot).toFixed(2)}
          </span>
        )}
        {Number(trip.client_balance_snapshot || 0) < -0.01 && (
          <span
            className="text-[10px] font-mono font-black uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/50 bg-emerald-500/15 text-emerald-300 whitespace-nowrap flex-shrink-0"
            title="El pasajero tiene crédito con la LLC (pagó por adelantado)"
            data-testid={`client-credit-badge-${trip.id}`}
          >
            crédito ${Math.abs(Number(trip.client_balance_snapshot)).toFixed(2)}
          </span>
        )}
        <ReceiptLink
          receipt={trip.investment_receipt}
          testId={`trip-receipt-link-${trip.id}`}
          label=""
        />
        <span className="text-sm font-black font-mono whitespace-nowrap flex-shrink-0" style={{ color: "#FBBF24" }}>
          ${Number(trip.price || 0).toFixed(0)}
        </span>
      </div>

      {/* Row 2: addresses + driver select (max 10 chars) + action buttons.
          Non-alias addresses are trimmed at "Naples" so a long ", FL 34116,
          EE. UU." tail doesn't push the action buttons off-screen. */}
      <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
        {trip.trip_type === "delivery" ? (
          <div className="flex items-center gap-1 text-[11px] min-w-0 flex-1 text-muted-foreground">
            <ShoppingCart className="w-3 h-3 text-accent flex-shrink-0" />
            <span className="truncate text-white/90" title={trip.items || undefined}>
              {(() => {
                // Truncate the item list to a max of 7 words on the dispatch
                // card. Long delivery orders were stretching the row so far
                // the action buttons on the right were unreachable. Full
                // list is still visible when opening the trip in the edit
                // dialog.
                const raw = trip.items || "Sin productos";
                const words = raw.trim().split(/\s+/);
                return words.length > 7 ? `${words.slice(0, 7).join(" ")}…` : raw;
              })()}
            </span>
            {trip.dropoff_address && (
              <>
                <span className="text-muted-foreground">→</span>
                <MapPin className="w-3 h-3 text-accent flex-shrink-0" />
                <span
                  className={`truncate ${dropoffAlias ? "text-primary font-bold" : "text-white/90"}`}
                  title={trip.dropoff_address}
                >
                  {dropoffAlias ? `★ ${dropoffAlias}` : truncateAtNaples(trip.dropoff_address)}
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1 text-[11px] min-w-0 flex-1 text-muted-foreground">
            <MapPin className="w-3 h-3 text-primary flex-shrink-0" />
            <span
              className={`truncate ${pickupAlias ? "text-primary font-bold" : "text-white/90"}`}
              title={trip.pickup_address}
            >
              {pickupAlias ? `★ ${pickupAlias}` : truncateAtNaples(trip.pickup_address)}
            </span>
            <span className="text-muted-foreground">→</span>
            <MapPin className="w-3 h-3 text-accent flex-shrink-0" />
            <span
              className={`truncate ${dropoffAlias ? "text-primary font-bold" : "text-white/90"}`}
              title={trip.dropoff_address}
            >
              {dropoffAlias ? `★ ${dropoffAlias}` : truncateAtNaples(trip.dropoff_address)}
            </span>
          </div>
        )}
        <Select value={trip.driver_id || "none"} onValueChange={assignDriver}>
          <SelectTrigger
            className="h-6 text-[11px] w-[104px] bg-input border-border px-2 flex-shrink-0"
            data-testid={`assign-driver-${trip.id}`}
            title={trip.driver_name || "Sin chofer"}
          >
            {/* Custom trigger content — driver names are truncated to 10
                chars visually so the row keeps a fixed compact width, no
                matter how long the driver's name is. */}
            <span className="truncate">
              {trip.driver_id
                ? (trip.driver_name || "").slice(0, 10)
                : "Sin chofer"}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sin chofer</SelectItem>
            {drivers.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {trip.payment_method && (
          <span
            className={`text-[10px] font-mono font-black uppercase tracking-wider px-1.5 py-0.5 rounded border whitespace-nowrap flex-shrink-0 ${
              trip.payment_method === "cash"
                ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                : trip.payment_method === "zelle"
                ? "border-sky-500/50 bg-sky-500/15 text-sky-300"
                : trip.payment_method === "credito"
                ? "border-violet-500/50 bg-violet-500/15 text-violet-300"
                : "border-border bg-muted text-muted-foreground"
            }`}
            title={`Método de pago: ${trip.payment_method}`}
            data-testid={`trip-payment-badge-${trip.id}`}
          >
            {trip.payment_method}
          </span>
        )}
        {["accepted", "arrived_pickup", "with_client", "pending_review"].includes(trip.status) && (
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const label = STATUS_LABELS[trip.status] || trip.status;
              if (!window.confirm(`¿Retroceder el estado del viaje desde "${label}"? Úsalo si el chofer tocó un botón por error.`)) return;
              try {
                const { data } = await api.post(`/trips/${trip.id}/revert-status`);
                toast.success(`Estado retrocedido a "${STATUS_LABELS[data.status] || data.status}"`);
                onReload();
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            data-testid={`revert-trip-${trip.id}`}
            className="border-border h-6 w-6 p-0 text-amber-400 hover:text-amber-300 flex-shrink-0"
            title="Retroceder estado (si el chofer tocó un botón por error)"
          >
            <Undo2 className="w-3 h-3" />
          </Button>
        )}
        {/* Single always-visible green checkmark — approves when the driver
            requested closure (pending_review) or force-closes an in-flight
            trip if the dispatcher needs to end it manually. Hidden only on
            terminal statuses (completed / cancelled) since History tab has
            its own actions. */}
        {(trip.status === "pending_review" ||
          ["assigned", "accepted", "arrived_pickup", "with_client"].includes(trip.status)) && (
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const isApproval = trip.status === "pending_review";
              const msg = isApproval
                ? "¿Aprobar y cerrar el viaje?"
                : `¿Guardar el viaje #${trip.trip_number || ""} como REALIZADO ahora?\n\n`
                  + "Esto lo cierra sin esperar que el chofer termine el flujo. "
                  + "Se sumará a la contabilidad. La acción no se puede deshacer.";
              if (!window.confirm(msg)) return;
              try {
                await api.post(`/trips/${trip.id}/status`, { status: "completed" });
                toast.success(isApproval ? "Viaje aprobado y cerrado" : "Viaje guardado como realizado");
                onReload();
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            data-testid={trip.status === "pending_review" ? `approve-trip-${trip.id}` : `save-trip-${trip.id}`}
            className="border-emerald-500/60 h-6 w-6 p-0 text-emerald-400 hover:text-emerald-300 hover:border-emerald-400 flex-shrink-0"
            title={trip.status === "pending_review" ? "Aprobar y cerrar" : "Guardar como viaje realizado"}
          >
            <CheckCircle2 className="w-3 h-3" />
          </Button>
        )}
        <StatusHistoryButton trip={trip} />
        {(() => {
          const lock = trip.editing_dispatcher;
          const mine = lock && me && lock.id === me.id;
          const lockedByOther = lock && !mine;
          if (lockedByOther) {
            return (
              <Button
                size="sm"
                variant="outline"
                onDoubleClick={() => onEdit({ force: true })}
                onClick={() =>
                  toast.warning(`${lock.name || "Otro dispatcher"} está editando este viaje. Doble clic para tomarlo.`)
                }
                data-testid={`edit-locked-${trip.id}`}
                title={`Bloqueado por ${lock.name}. Doble clic para tomar el control.`}
                className="border-red-500/60 text-red-400 hover:bg-red-500/10 h-6 w-6 p-0 flex-shrink-0"
              >
                <Lock className="w-3 h-3" />
              </Button>
            );
          }
          return (
            <NotesHoverTip trip={trip} testId={`notes-tip-${trip.id}`}>
              <Button size="sm" variant="outline" onClick={() => onEdit()} data-testid={`edit-trip-${trip.id}`} className="border-border h-6 w-6 p-0 flex-shrink-0">
                <Pencil className="w-3 h-3" />
              </Button>
            </NotesHoverTip>
          );
        })()}
        <Button size="sm" variant="outline" onClick={onDelete} data-testid={`delete-trip-${trip.id}`} className="border-border h-6 w-6 p-0 text-destructive hover:text-destructive flex-shrink-0" title="Cancelar y mover a Historial">
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </Card>
  );
}
